import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ChevronDown, ChevronLeft, ChevronRight, Download } from "lucide-react";
import { useState } from "react";

import { isCycleClosed } from "@/utils/fiscal-cycle";
import { DASHBOARD_LABELS } from "../messages";

const MONTH_LABELS: readonly string[] = [
  "Jan", "Fev", "Mar", "Abr", "Mai", "Jun",
  "Jul", "Ago", "Set", "Out", "Nov", "Dez",
];

const MIN_YEAR: number = 2020;

export interface ExportMenuItem {
  key: string;
  label: string;
  onSelect: () => void;
}

export interface ConsolidatedExportItem {
  key: string;
  label: string;
  onSelect: (year: number, month: number) => void;
}

interface ExportTablesMenuProps {
  items: readonly ExportMenuItem[];
  consolidatedItems: readonly ConsolidatedExportItem[];
}

interface FiscalCyclePickerProps {
  onSelect: (year: number, month: number) => void;
}

function FiscalCyclePicker({ onSelect }: FiscalCyclePickerProps): JSX.Element {
  const now: Date = new Date();
  const maxYear: number = now.getFullYear();
  const [year, setYear] = useState<number>(maxYear);

  const isNotClosedYet = (monthIndex: number): boolean =>
    !isCycleClosed(year, monthIndex, now);

  return (
    <div className="mt-2 rounded-md border p-2">
      <div className="flex items-center justify-between">
        <Button
          variant="ghost_none"
          className="h-6 w-6 border-none bg-transparent p-0"
          aria-label="Ano anterior"
          disabled={year <= MIN_YEAR}
          onClick={() => setYear((current: number) => current - 1)}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="text-sm font-medium">{year}</span>
        <Button
          variant="ghost_none"
          className="h-6 w-6 border-none bg-transparent p-0"
          aria-label="Próximo ano"
          disabled={year >= maxYear}
          onClick={() => setYear((current: number) => current + 1)}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
      <div className="mt-2 grid grid-cols-3 gap-1">
        {MONTH_LABELS.map((month: string, index: number) => (
          <button
            key={month}
            type="button"
            disabled={isNotClosedYet(index)}
            onClick={() => onSelect(year, index + 1)}
            className="rounded px-2 py-1 text-xs text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:bg-transparent"
          >
            {month}
          </button>
        ))}
      </div>
    </div>
  );
}

export function ExportTablesMenu({
  items,
  consolidatedItems,
}: ExportTablesMenuProps): JSX.Element {
  const [openKey, setOpenKey] = useState<string | null>(null);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          size="sm"
          className="h-8 bg-[#F6D040] text-[#613F4F] hover:bg-[#F6D040]/90"
        >
          <Download className="mr-2 h-4 w-4" />
          {DASHBOARD_LABELS.EXPORT_MENU}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[264px] p-2">
        {items.map((item: ExportMenuItem) => (
          <button
            key={item.key}
            type="button"
            onClick={item.onSelect}
            className="w-full rounded px-2 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
          >
            {item.label}
          </button>
        ))}

        {consolidatedItems.length > 0 && <hr className="my-2" />}

        {consolidatedItems.map((item: ConsolidatedExportItem) => (
          <div key={item.key}>
            <button
              type="button"
              onClick={() =>
                setOpenKey((current: string | null) =>
                  current === item.key ? null : item.key
                )
              }
              className="flex w-full items-center justify-between rounded px-2 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
            >
              {item.label}
              <ChevronDown
                className={`h-4 w-4 transition-transform ${
                  openKey === item.key ? "rotate-180" : ""
                }`}
              />
            </button>
            {openKey === item.key && (
              <FiscalCyclePicker onSelect={item.onSelect} />
            )}
          </div>
        ))}
      </PopoverContent>
    </Popover>
  );
}
